import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Check, Loader2 } from 'lucide-react';

import { cn } from '../../lib/cn';

/**
 * SaveStateIndicator — small status chip showing auto-save state.
 *
 *   Saved 3s ago       (idle, no pending, no error)
 *   Saving…            (debounce armed OR PATCH in flight)
 *   ⚠ Save failed       (last PATCH errored)
 *
 * Auto-save is the only mode in v1 — see useAnnotatorState.updateBox for
 * the debounce + coalescing logic. This indicator just exposes that state
 * to the UI so the user can see at a glance that their edits are durable.
 *
 * Ctrl+S flushes pending debounces immediately; the parent (Annotator)
 * wires that to useAnnotatorState.flushPendingSaves.
 */
interface SaveStateIndicatorProps {
    isDarkMode?: boolean;
    /** Number of edits whose debounce timer hasn't fired yet. */
    pendingCount: number;
    /** Number of PATCHes currently in flight to the backend. */
    inFlightCount: number;
    /** Wall-clock ms of last successful save. */
    lastSavedAt: number | null;
    /** Error message of last failed save, if any (cleared on next success). */
    lastSaveError: string | null;
    /** Manual flush — sends all pending edits immediately. */
    onFlush?: () => void;
}

export default function SaveStateIndicator({
    isDarkMode = false,
    pendingCount,
    inFlightCount,
    lastSavedAt,
    lastSaveError,
    onFlush,
}: SaveStateIndicatorProps) {
    const { t } = useTranslation('canvas');
    const isSaving = pendingCount > 0 || inFlightCount > 0;

    // Tick once a minute so 'Saved 3s ago' refreshes without firing on every
    // canvas-state change.
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((n) => n + 1), 30_000);
        return () => clearInterval(id);
    }, []);

    let icon: React.ReactNode;
    let text: string;
    let tone: 'idle' | 'saving' | 'error';
    if (lastSaveError && !isSaving) {
        icon = <AlertCircle className="h-3.5 w-3.5" />;
        text = t('save_failed', { defaultValue: 'Save failed' });
        tone = 'error';
    } else if (isSaving) {
        icon = <Loader2 className="h-3.5 w-3.5 animate-spin" />;
        text = t('saving', { defaultValue: 'Saving…' });
        tone = 'saving';
    } else if (lastSavedAt) {
        icon = <Check className="h-3.5 w-3.5" />;
        text = t('saved_relative', { defaultValue: 'Saved {{when}}', when: relativeTime(lastSavedAt) });
        tone = 'idle';
    } else {
        icon = <Check className="h-3.5 w-3.5" />;
        text = t('saved_all_changes', { defaultValue: 'All changes saved' });
        tone = 'idle';
    }

    const toneCls = {
        idle: isDarkMode ? 'text-gray-400' : 'text-gray-500',
        saving: isDarkMode ? 'text-primary-300' : 'text-primary-700',
        error: isDarkMode ? 'text-amber-400' : 'text-amber-700',
    }[tone];

    return (
        <button
            type="button"
            onClick={onFlush}
            disabled={!onFlush || (!isSaving && !lastSaveError)}
            title={onFlush ? t('flush_pending_hint', { defaultValue: 'Ctrl+S — save now' }) : undefined}
            className={cn(
                'inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] transition-colors',
                'disabled:cursor-default',
                toneCls,
                !!onFlush && (isSaving || lastSaveError) && (isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'),
            )}
        >
            {icon}
            <span className="font-medium">{text}</span>
        </button>
    );
}

/** Human-friendly elapsed time — "just now" / "5s ago" / "2m ago". */
function relativeTime(t: number): string {
    const diff = Math.max(0, Date.now() - t);
    if (diff < 3000) return 'just now';
    if (diff < 60_000) return `${Math.round(diff / 1000)}s ago`;
    if (diff < 60 * 60 * 1000) return `${Math.round(diff / 60_000)}m ago`;
    return `${Math.round(diff / (60 * 60_000))}h ago`;
}
